<template>
  <div class="tw-relative">
    <div
      class="tw-relative tw-overflow-hidden"
      :style="{ 'max-height': `${sliderHeight}px` }"
    >
      <CruiseSliderArrowButton
        v-show="showTopArrow"
        arrow-type="top"
        @click="scrollCruiseDates('previous')"
      />
      <div
        id="cruise-slide-wrapper"
        ref="cruiseSlideWrap"
        class="tw-relative tw-transition-top tw-duration-700 tw-ease-in-out"
        :style="{ top: `${topOffset}px` }"
      >
        <CruiseSlideCard
          v-for="(cruise, index) in cruiseData"
          :key="index"
          class="searchResult_DayItinerary"
          ref="cruiseSlideCard"
          :card-data="cruise"
          :cruise-index="index"
          :is-card-active="activeClass(index)"
          :text-labels="textLabels"
          @click="chooseCruise(index)"
        />
      </div>
      <CruiseSliderArrowButton
        v-show="showBottomArrow"
        arrow-type="bottom"
        @click="scrollCruiseDates('next')"
      />
    </div>
  </div>
</template>

<script>
import CruiseSlideCard from "@/stories/Components/CruiseItineraryDetail/Elements/CruiseSlideCard/CruiseSlideCard";
import CruiseSliderArrowButton from "@/stories/Components/CruiseItineraryDetail/Elements/desktop/CruiseSliderArrowButton/CruiseSliderArrowButton";
import { EventBus } from "@/helpers/event-bus";

const VERTICAL_CARDS_MARGIN = 10;

export default {
  name: "CruiseSlider",

  components: {
    CruiseSlideCard,
    CruiseSliderArrowButton,
  },

  props: {
    activeCruiseIndex: {
      type: Number,
      required: true,
      default: 0,
    },
    cruiseData: {
      type: Array,
      required: true,
      default: () => [],
    },
    slidesCount: {
      type: Number,
      required: true,
    },
    textLabels: {
      type: Object,
      required: true,
      default: () => {},
    },
    sliderHeight: {
      type: Number,
      required: true,
      default: 333,
    },
  },

  computed: {
    cruiseSliderWrapper() {
      return this.$refs.cruiseSlideWrap;
    },
    cruiseSliderFullHeight() {
      return this.$refs.cruiseSlideWrap?.clientHeight;
    },
    calculatedGeneralSlidesScrolls() {
      return Math.ceil(
        this.cruiseSliderFullHeight /
          (this.sliderHeight - this.cruiseSlideCardHeight)
      );
    },
    calculatedFullSlidesScrolls() {
      return Math.floor(
        this.cruiseSliderFullHeight /
          (this.sliderHeight - this.cruiseSlideCardHeight)
      );
    },
    calculatedLastSlideTopOffset() {
      return (
        this.cruiseSliderFullHeight -
        this.calculatedFullSlidesScrolls *
          (this.sliderHeight - this.cruiseSlideCardHeight)
      );
    },
    cruiseSlideCardHeight() {
      return document.getElementsByClassName("cruise-slide")?.[0]?.offsetHeight;
    },
    sliderVisibleCardsCount() {
      return Math.floor(
        this.sliderHeight / (this.cruiseSlideCardHeight + VERTICAL_CARDS_MARGIN)
      );
    },
  },

  watch: {
    sliderHeight(newVal) {
      if (newVal) {
        this.checkIfBottomArrowIsShown();
      }
    },
  },

  data() {
    return {
      topOffset: 0,
      showTopArrow: false,
      showBottomArrow: false,
      scrolledSlidesCount: 0,
      VERTICAL_CARDS_MARGIN,
    };
  },

  created() {
    EventBus.$on("changeDate", this.changeActiveCruise);
  },

  mounted() {
    this.checkIfBottomArrowIsShown();
  },

  methods: {
    scrollCruiseDates(type) {
      type === "next" ? this.slideNext() : this.slidePrev();
    },
    slideNext() {
      const isLastSlide =
        this.cruiseSliderFullHeight +
          this.topOffset -
          (this.sliderHeight - this.cruiseSlideCardHeight) <=
        this.sliderHeight;
      this.showTopArrow = true;
      this.scrolledSlidesCount += 1;

      if (isLastSlide) {
        this.topOffset -=
          this.cruiseSliderFullHeight + this.topOffset - this.sliderHeight;
        this.showBottomArrow = false;
      } else {
        this.topOffset -= this.sliderHeight - this.cruiseSlideCardHeight;
      }

      // last bottom slide
      if (
        -(this.topOffset - this.sliderHeight) === this.cruiseSliderFullHeight
      ) {
        this.showBottomArrow = false;
      }
    },
    slidePrev() {
      const isLastSlide =
        this.topOffset > -(this.sliderHeight - this.cruiseSlideCardHeight);
      this.showBottomArrow = true;
      this.scrolledSlidesCount -= 1;

      if (isLastSlide) {
        this.topOffset += -this.topOffset;
        this.showTopArrow = false;
      } else {
        this.topOffset += this.sliderHeight - this.cruiseSlideCardHeight;
        this.showTopArrow = true;
      }

      if (this.topOffset === 0) {
        this.showTopArrow = false;
      }
    },
    chooseCruise(cruiseIndex) {
      this.$emit("change-cruise-index", cruiseIndex);
    },
    activeClass(index) {
      return this.activeCruiseIndex === index;
    },
    changeActiveCruise(changeType) {
      const activeSlide = document.getElementsByClassName(
        "cruise-slide__data--active"
      )?.[0];

      // checks if it should scroll to the 'NEXT' slide
      const autoscrollToNextSlide =
        changeType === "next" &&
        activeSlide.offsetTop +
          this.cruiseSlideCardHeight +
          VERTICAL_CARDS_MARGIN +
          this.topOffset >
          this.sliderHeight - this.cruiseSlideCardHeight;

      // on active slide selected 'offsetTop' prop show the value of previous active slide.
      // To avoid bugs the calculation below is required
      const actualActiveSlideTopOffset =
        changeType === "next"
          ? activeSlide.offsetTop + this.cruiseSlideCardHeight
          : activeSlide.offsetTop - this.cruiseSlideCardHeight;

      // checks if it should scroll to the 'Previous' slide
      const autoscrollToPrevSlide =
        changeType === "prev" &&
        actualActiveSlideTopOffset + this.topOffset < 0;

      if (autoscrollToNextSlide) {
        this.slideNext();
      } else if (autoscrollToPrevSlide) {
        this.slidePrev();
      }

      const cruiseNewIndex =
        changeType === "next"
          ? this.activeCruiseIndex + 1
          : this.activeCruiseIndex - 1;
      this.chooseCruise(cruiseNewIndex);
    },
    checkIfBottomArrowIsShown() {
      this.showBottomArrow =
        this.cruiseSliderFullHeight > this.sliderHeight &&
        this.cruiseData?.length > this.sliderVisibleCardsCount;
    },
  },

  beforeDestroy() {
    EventBus.$off("changeDate");
  },
};
</script>
